import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const LABEL = { 1: "Poor", 2: "Below expectations", 3: "Fine", 4: "Good", 5: "Excellent" };

const StarRow = ({ label, value, onChange, testid }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <div className="mt-1.5 flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange(i)} data-testid={`${testid}-${i}`}
          className="transition-transform hover:scale-110">
          <Star className={cn("h-8 w-8", i <= value ? "fill-primary text-primary" : "text-muted-foreground/35")} />
        </button>
      ))}
      {value > 0 && <span className="ml-2 text-sm font-medium text-primary">{LABEL[value]}</span>}
    </div>
  </div>
);

export default function DemoFeedback() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ rating: 0, trainer_rating: 0, content_rating: 0, comments: "", would_recommend: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/demo-feedback/${token}`)
      .then((r) => { setInfo(r.data); setDone(r.data.submitted); })
      .catch((e) => setError(errMsg(e, "This feedback link is not valid any more.")));
  }, [token]);

  const submit = async () => {
    if (!form.rating) return setError("Please tap a star to rate the session.");
    setBusy(true);
    setError("");
    try {
      await api.post(`/public/demo-feedback/${token}`, form);
      setDone(true);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grain min-h-screen bg-gradient-panel px-4 py-10">
      <div className="mx-auto max-w-lg">
        {error && !info && (
          <div className="rounded-2xl border border-destructive/40 bg-card p-8 text-center" data-testid="feedback-error">
            <p className="font-display text-lg font-bold">{error}</p>
          </div>
        )}

        {!info && !error && (
          <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        )}

        {info && (
          <div className="rounded-2xl border border-border bg-card p-7 shadow-lg">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">{info.company_name}</p>
            <h1 className="mt-1 font-display text-2xl font-bold">
              {done ? "Thank you for your feedback" : `How was your ${info.kind.toLowerCase()}?`}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {info.kind} {info.number} &middot; {info.dates} &middot; {info.days} day(s)
              {info.team?.length ? ` \u00b7 conducted by ${info.team.join(", ")}` : ""}
            </p>

            {done ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-success/40 bg-success-soft p-4"
                data-testid="feedback-done">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                <p className="text-sm">
                  Your rating has reached our team and helps us get better. If you need anything else,
                  simply reply to our email &mdash; we are always happy to help.
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-5">
                <StarRow label="Overall experience" value={form.rating} testid="star-overall"
                  onChange={(v) => setForm({ ...form, rating: v })} />
                <StarRow label="Our engineer / trainer" value={form.trainer_rating} testid="star-trainer"
                  onChange={(v) => setForm({ ...form, trainer_rating: v })} />
                <StarRow label="Content and usefulness" value={form.content_rating} testid="star-content"
                  onChange={(v) => setForm({ ...form, content_rating: v })} />

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Anything you would like us to improve?
                  </p>
                  <Textarea rows={3} className="mt-1.5" value={form.comments} data-testid="feedback-comments"
                    onChange={(e) => setForm({ ...form, comments: e.target.value })}
                    placeholder="Optional, but very useful for us" />
                </div>

                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.would_recommend} data-testid="feedback-recommend"
                    onChange={(e) => setForm({ ...form, would_recommend: e.target.checked })}
                    className="h-4 w-4 accent-primary" />
                  I would recommend {info.company_name} to others
                </label>

                {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                <Button className="w-full" size="lg" onClick={submit} disabled={busy} data-testid="feedback-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send my feedback
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
